/** Limits (E12-T03, E12-T09): the tenant's own with what it uses, and a form for a child's. */
import { useId, useState, type FormEvent } from "react";
import type { Schemas } from "../api/client";
import { useFormat, useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";

type LimitsOut = Schemas["LimitsOut"];
type Usage = Schemas["TenantLimitsOut"];
const NUMBERS = ["max_concurrent_calls", "max_cps", "max_monthly_minutes", "recording_retention_days"] as const;

function shown(t: ReturnType<typeof useT>, value: number | null | undefined) {
  return value === null || value === undefined ? t("limits.unlimited") : String(value);
}

/** A meter for a limit and its use: a bar, and the numbers in words. */
function Meter({ label, used, limit }: { label: string; used: number; limit: number | null | undefined }) {
  const t = useT();
  const share = limit ? Math.min(1, used / limit) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="tabular-nums text-[var(--color-muted)]">
          {limit ? t("limits.ofLimit", { used, limit }) : t("limits.usedNoLimit", { used })}
        </span>
      </div>
      {limit ? (
        <div role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={limit} aria-valuenow={used}
          className="h-2 overflow-hidden rounded bg-[var(--color-surface)]">
          <div className={`h-full ${share >= 1 ? "bg-red-600" : share >= 0.8 ? "bg-amber-500" : "bg-[var(--color-primary)]"}`}
            style={{ width: `${share * 100}%` }} />
        </div>
      ) : null}
    </div>
  );
}

export function LimitsAndUsage({ current }: { current: Usage }) {
  const t = useT();
  const format = useFormat();
  const { limits, usage } = current;
  return (
    <div className="max-w-2xl space-y-4">
      {current.status === "suspended" && (
        <p role="alert" className="rounded-md border border-red-600 p-3 text-sm">{t("limits.suspended")}</p>
      )}
      <Meter label={t("limits.concurrent")} used={usage.concurrent_calls} limit={limits.max_concurrent_calls} />
      <Meter label={t("limits.minutes", { since: format.date(usage.month_start) })}
        used={Math.round(usage.minutes_this_month)} limit={limits.max_monthly_minutes} />
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
        <dt className="text-[var(--color-muted)]">{t("limits.field.max_cps")}</dt>
        <dd>{shown(t, limits.max_cps)}</dd>
        <dt className="text-[var(--color-muted)]">{t("limits.field.allowed_countries")}</dt>
        <dd>{limits.allowed_countries.length ? limits.allowed_countries.join(", ") : t("limits.anyCountry")}</dd>
        <dt className="text-[var(--color-muted)]">{t("limits.field.blocked_prefixes")}</dt>
        <dd>{limits.blocked_prefixes.length ? limits.blocked_prefixes.join(", ") : t("limits.none")}</dd>
        <dt className="text-[var(--color-muted)]">{t("limits.field.recording_retention_days")}</dt>
        <dd>{shown(t, limits.recording_retention_days)}</dd>
      </dl>
      <p className="text-xs text-[var(--color-muted)]">{t("limits.hint")}</p>
    </div>
  );
}

/** A one-line summary of a child's limits, for a table. */
export function limitsSummary(t: ReturnType<typeof useT>, limits: LimitsOut): string {
  const parts = [
    limits.max_concurrent_calls != null && t("limits.short.concurrent", { n: limits.max_concurrent_calls }),
    limits.max_monthly_minutes != null && t("limits.short.minutes", { n: limits.max_monthly_minutes }),
    limits.allowed_countries.length > 0 && limits.allowed_countries.join(", "),
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : t("limits.unlimited");
}

const list = (text: string) => text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);

/** A child tenant's limits, all of them (a PUT replaces them): empty means unlimited, or its reseller's. */
export function LimitsForm({ initial, busy, onSave }: {
  initial: LimitsOut;
  busy: boolean;
  onSave: (limits: Schemas["Limits"]) => void;
}) {
  const t = useT();
  const id = useId();
  const [numbers, setNumbers] = useState<Record<string, string>>(
    Object.fromEntries(NUMBERS.map((k) => [k, initial[k] == null ? "" : String(initial[k])])),
  );
  const [countries, setCountries] = useState(initial.allowed_countries.join(", "));
  const [prefixes, setPrefixes] = useState(initial.blocked_prefixes.join(", "));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const out: Record<string, unknown> = {
      allowed_countries: list(countries).map((c) => c.toUpperCase()),
      blocked_prefixes: list(prefixes),
    };
    for (const k of NUMBERS) if (numbers[k]) out[k] = Number(numbers[k]);
    onSave(out as Schemas["Limits"]);
  };
  const field = "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {NUMBERS.map((k) => (
          <div key={k} className="space-y-1">
            <label htmlFor={`${id}-${k}`} className="block text-sm font-medium">{t(`limits.field.${k}` as MessageId)}</label>
            <input id={`${id}-${k}`} type="number" min={k === "recording_retention_days" ? 1 : 0} inputMode="numeric"
              value={numbers[k]} placeholder={t("limits.unlimited")}
              onChange={(e) => setNumbers({ ...numbers, [k]: e.target.value })} className={field} />
          </div>
        ))}
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-countries`} className="block text-sm font-medium">{t("limits.field.allowed_countries")}</label>
        <input id={`${id}-countries`} value={countries} onChange={(e) => setCountries(e.target.value)}
          placeholder="IN, AE" aria-describedby={`${id}-countries-hint`} className={field} />
        <p id={`${id}-countries-hint`} className="text-xs text-[var(--color-muted)]">{t("limits.countriesHint")}</p>
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-prefixes`} className="block text-sm font-medium">{t("limits.field.blocked_prefixes")}</label>
        <input id={`${id}-prefixes`} value={prefixes} onChange={(e) => setPrefixes(e.target.value)}
          placeholder="+91140" className={field} />
      </div>
      <p className="text-xs text-[var(--color-muted)]">{t("limits.childHint")}</p>
      <Button type="submit" disabled={busy}>{t("limits.save")}</Button>
    </form>
  );
}
